import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiGet } from '@/utils/api';

// Helper to convert any frequency to monthly amount for totals
function getMonthlyAmount(amount, frequency) {
  const freq = (frequency || 'Monthly').toLowerCase();
  if (freq === 'annually' || freq === 'yearly' || freq === 'annual') return amount / 12;
  if (freq === 'quarterly') return amount / 3;
  if (freq === 'weekly') return amount * 4.33;
  if (freq === 'bi-weekly' || freq === 'biweekly') return amount * 2.17;
  return amount; // Monthly or default
}

// Helper to format amount with CORRECT frequency suffix
function formatAmountWithFrequency(amount, frequency) {
  const freq = (frequency || 'Monthly').toLowerCase();
  if (freq === 'annually' || freq === 'yearly' || freq === 'annual') return `$${amount.toFixed(2)}/yr`;
  if (freq === 'quarterly') return `$${amount.toFixed(2)}/qtr`;
  if (freq === 'weekly') return `$${amount.toFixed(2)}/wk`;
  if (freq === 'bi-weekly' || freq === 'biweekly') return `$${amount.toFixed(2)}/2wk`;
  return `$${amount.toFixed(2)}/mo`;
}

// Helper to get frequency display label
function getFrequencyLabel(frequency) {
  const freq = (frequency || 'Monthly').toLowerCase();
  if (freq === 'annually' || freq === 'yearly' || freq === 'annual') return 'Annual';
  if (freq === 'quarterly') return 'Quarterly';
  if (freq === 'weekly') return 'Weekly';
  if (freq === 'bi-weekly' || freq === 'biweekly') return 'Bi-Weekly';
  return null; // Don't show label for monthly (default)
}

function QuickWins() {
  const [bills, setBills] = useState([]);
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [billsRes, debtsRes] = await Promise.all([
          apiGet('/bills'),
          apiGet('/debt')  // Correct endpoint
        ]);
        
        if (billsRes.ok) {
          const data = await billsRes.json();
          setBills(data);
        }
        if (debtsRes.ok) {
          const data = await debtsRes.json();
          setDebts(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filter bills by category
  const subscriptions = bills.filter(b => b.is_subscription || b.category === 'ENTERTAINMENT' || b.category === 'Subscriptions');
  const phoneBills = bills.filter(b => b.category === 'PHONE' || b.name.toLowerCase().includes('phone'));
  const insuranceBills = bills.filter(b => b.category === 'Insurance' || b.name.toLowerCase().includes('insurance'));
  const highInterestDebts = debts.filter(d => d.apr && d.apr > 15).sort((a, b) => b.apr - a.apr);

  // Calculate totals (converted to monthly)
  const subscriptionTotal = subscriptions.reduce((sum, b) => sum + getMonthlyAmount(b.amount, b.frequency), 0);
  const phoneTotal = phoneBills.reduce((sum, b) => sum + getMonthlyAmount(b.amount, b.frequency), 0);
  const insuranceTotal = insuranceBills.reduce((sum, b) => sum + getMonthlyAmount(b.amount, b.frequency), 0);
  
  // Calculate debt stats for personalized tips
  const totalDebtBalance = debts.reduce((sum, d) => sum + (d.balance || 0), 0);
  const avgAPR = debts.length > 0 ? debts.reduce((sum, d) => sum + (d.apr || 0), 0) / debts.length : 0;
  const highestAPR = debts.length > 0 ? Math.max(...debts.map(d => d.apr || 0)) : 0;
  const lowestAPR = debts.length > 0 ? Math.min(...debts.filter(d => d.apr > 0).map(d => d.apr)) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your bills...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <span className="text-3xl">💡</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900" data-testid="quick-wins-title">Money Tips</h1>
        <p className="text-gray-600 mt-2">Actionable ways to reduce your bills and save money</p>
      </div>

      {/* Quick Money Wins - NOW AT TOP */}
      <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
          <span className="text-2xl">⚡</span> Quick Money Wins
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h4 className="font-semibold text-gray-900 mb-2">🔄 Monthly Review</h4>
            <p className="text-sm text-gray-600">Set a calendar reminder to review all recurring charges on the 1st of each month</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h4 className="font-semibold text-gray-900 mb-2">📞 Negotiate Annually</h4>
            <p className="text-sm text-gray-600">Call each service provider once a year to ask for better rates or promotions</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h4 className="font-semibold text-gray-900 mb-2">🆓 Free Trials Trap</h4>
            <p className="text-sm text-gray-600">Set a reminder 2 days before any free trial ends to decide if you want to keep it</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h4 className="font-semibold text-gray-900 mb-2">💳 Check Your Statements</h4>
            <p className="text-sm text-gray-600">Look for forgotten subscriptions, price increases, or fees you didn't notice</p>
          </div>
        </div>
      </div>

      {/* Scroll Prompt */}
      <div className="text-center py-2">
        <p className="text-gray-500 text-sm flex items-center justify-center gap-2">
          <span>👇</span> Scroll down for personalized insights based on YOUR bills <span>👇</span>
        </p>
      </div>

      {/* Subscriptions Section */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="bg-gradient-to-r from-purple-500 to-violet-600 p-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            🔄 Subscriptions
            {subscriptionTotal > 0 && (
              <span className="text-sm font-normal bg-white/20 px-3 py-1 rounded-full">
                ~${subscriptionTotal.toFixed(2)}/mo
              </span>
            )}
          </h2>
        </div>
        <div className="p-6">
          {subscriptions.length > 0 ? (
            <div className="space-y-3 mb-4">
              {subscriptions.map((sub) => {
                const freqLabel = getFrequencyLabel(sub.frequency);
                return (
                  <div key={sub.id} className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{sub.name}</span>
                      {freqLabel && (
                        <span className="text-xs bg-purple-200 text-purple-700 px-2 py-0.5 rounded-full">
                          {freqLabel}
                        </span>
                      )}
                    </div>
                    <span className="text-purple-700 font-bold">{formatAmountWithFrequency(sub.amount, sub.frequency)}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 mb-4">No subscriptions tracked yet. Mark bills as subscriptions to see them here!</p>
          )}
          
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
            <h3 className="font-bold text-purple-900 mb-2">💰 How to Save:</h3>
            <ul className="space-y-2 text-sm text-purple-800">
              <li>• <strong>Audit monthly:</strong> Review all subscriptions - cancel anything you haven't used in 30 days</li>
              <li>• <strong>Rotate services:</strong> Subscribe to one streaming service at a time, binge, then switch</li>
              <li>• <strong>Annual discounts:</strong> Many services offer 15-20% off for annual payments</li>
              <li>• <strong>Share plans:</strong> Family/group plans for Spotify, YouTube, Netflix cost less per person</li>
              <li>• <strong>Free alternatives:</strong> Libraries offer free streaming (Libby, Hoopla, Kanopy)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Phone Bills Section */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="bg-gradient-to-r from-blue-500 to-cyan-600 p-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            📱 Phone Bills
            {phoneTotal > 0 && (
              <span className="text-sm font-normal bg-white/20 px-3 py-1 rounded-full">
                ~${phoneTotal.toFixed(2)}/mo
              </span>
            )}
          </h2>
        </div>
        <div className="p-6">
          {phoneBills.length > 0 ? (
            <div className="space-y-3 mb-4">
              {phoneBills.map((bill) => {
                const freqLabel = getFrequencyLabel(bill.frequency);
                return (
                  <div key={bill.id} className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{bill.name}</span>
                      {freqLabel && (
                        <span className="text-xs bg-blue-200 text-blue-700 px-2 py-0.5 rounded-full">
                          {freqLabel}
                        </span>
                      )}
                    </div>
                    <span className="text-blue-700 font-bold">{formatAmountWithFrequency(bill.amount, bill.frequency)}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 mb-4">No phone bills tracked. Add your phone bills to get tips!</p>
          )}
          
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h3 className="font-bold text-blue-900 mb-2">💰 How to Save:</h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li>• <strong>MVNOs (Mobile Virtual Network Operators):</strong> Mint Mobile, Visible, US Mobile use the same networks as big carriers for 50-70% less</li>
              <li>• <strong>Review your data usage:</strong> Most people overestimate - a lower data plan might work fine</li>
              <li>• <strong>Autopay discounts:</strong> Most carriers offer $5-10/month off for autopay + paperless</li>
              <li>• <strong>Employee/student discounts:</strong> Many employers and schools have carrier partnerships</li>
              <li>• <strong>Negotiate annually:</strong> Call retention and mention competitor pricing</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Insurance Section */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="bg-gradient-to-r from-emerald-500 to-green-600 p-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            🛡️ Insurance
            {insuranceTotal > 0 && (
              <span className="text-sm font-normal bg-white/20 px-3 py-1 rounded-full">
                ~${insuranceTotal.toFixed(2)}/mo
              </span>
            )}
          </h2>
        </div>
        <div className="p-6">
          {insuranceBills.length > 0 ? (
            <div className="space-y-3 mb-4">
              {insuranceBills.map((bill) => {
                const freqLabel = getFrequencyLabel(bill.frequency);
                return (
                  <div key={bill.id} className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{bill.name}</span>
                      {freqLabel && (
                        <span className="text-xs bg-emerald-200 text-emerald-700 px-2 py-0.5 rounded-full">
                          {freqLabel}
                        </span>
                      )}
                    </div>
                    <span className="text-emerald-700 font-bold">{formatAmountWithFrequency(bill.amount, bill.frequency)}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 mb-4">No insurance bills tracked. Add them to see savings tips!</p>
          )}
          
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <h3 className="font-bold text-emerald-900 mb-2">💰 How to Save:</h3>
            <ul className="space-y-2 text-sm text-emerald-800">
              <li>• <strong>Shop around annually:</strong> Get 3-5 quotes every year - loyalty doesn't pay with insurance</li>
              <li>• <strong>Bundle policies:</strong> Combining auto + home/renters can save 10-25%</li>
              <li>• <strong>Raise deductibles:</strong> Higher deductible = lower premium (if you have emergency fund)</li>
              <li>• <strong>Ask about discounts:</strong> Safe driver, good student, low mileage, anti-theft devices</li>
              <li>• <strong>Review coverage:</strong> Drop collision/comprehensive on older cars worth less than $5K</li>
              <li>• <strong>Pay annually:</strong> Avoid monthly billing fees (can save $50-100/year)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Interest Rates Section */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="bg-gradient-to-r from-red-500 to-orange-500 p-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            📈 High Interest Debt
            {debts.length > 0 && (
              <span className="text-sm font-normal bg-white/20 px-3 py-1 rounded-full">
                {highInterestDebts.length > 0 
                  ? `${highInterestDebts.length} debt${highInterestDebts.length > 1 ? 's' : ''} over 15% APR`
                  : 'All rates look good!'
                }
              </span>
            )}
          </h2>
        </div>
        <div className="p-6">
          {/* Summary Stats - Only shown when user has debts */}
          {debts.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-red-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-red-700">${totalDebtBalance.toLocaleString()}</p>
                <p className="text-xs text-red-600">Total Balance</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-orange-700">{highestAPR.toFixed(1)}%</p>
                <p className="text-xs text-orange-600">Highest APR</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-amber-700">{avgAPR.toFixed(1)}%</p>
                <p className="text-xs text-amber-600">Avg APR</p>
              </div>
              <div className="bg-green-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-green-700">{debts.length}</p>
                <p className="text-xs text-green-600">Total Debts</p>
              </div>
            </div>
          )}
          
          {/* How to Save - Always show tips, personalize if debt data exists */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <h3 className="font-bold text-red-900 mb-2">💰 How to Save:</h3>
            <ul className="space-y-2 text-sm text-red-800">
              {/* Personalized tips when debt data exists */}
              {highestAPR > 20 && (
                <li>• <strong>🚨 Priority Alert:</strong> You have debt at {highestAPR.toFixed(1)}% APR - focus extra payments here first (Avalanche method)</li>
              )}
              {highestAPR > 15 && highestAPR <= 20 && (
                <li>• <strong>Balance transfer opportunity:</strong> Look for 0% APR cards to move your {highestAPR.toFixed(1)}% debt - could save hundreds</li>
              )}
              {totalDebtBalance > 5000 && (
                <li>• <strong>Consolidation option:</strong> With ${totalDebtBalance.toLocaleString()} in debt, a personal loan at ~10% could save you vs {avgAPR.toFixed(1)}% average</li>
              )}
              {debts.length > 3 && (
                <li>• <strong>Simplify:</strong> You have {debts.length} debts - consider consolidating to 1-2 payments for easier tracking</li>
              )}
              {highestAPR - lowestAPR > 10 && lowestAPR > 0 && (
                <li>• <strong>Rate gap:</strong> Your rates range from {lowestAPR.toFixed(1)}% to {highestAPR.toFixed(1)}% - prioritize the highest first</li>
              )}
              
              {/* Universal tips always shown */}
              <li>• <strong>Balance transfer cards:</strong> Many offer 0% APR for 12-21 months (watch the transfer fee)</li>
              <li>• <strong>Debt consolidation:</strong> Personal loans often have lower rates than credit cards</li>
              <li>• <strong>Call and negotiate:</strong> Ask your card issuer for a lower rate - success rate is ~70%</li>
              <li>• <strong>Avalanche method:</strong> Pay minimums on all, extra on highest APR debt first</li>
              <li>• <strong>Refinance options:</strong> Credit score improved? Refinance auto loans, mortgages, student loans</li>
              <li>• <strong>Pay more than minimum:</strong> Even $20 extra/month significantly reduces interest paid</li>
            </ul>
            
            {debts.length === 0 && (
              <div className="mt-4 pt-4 border-t border-red-200">
                <p className="text-red-600 text-sm mb-2">
                  💡 Add your debts in the Debt Calculator for personalized tips based on YOUR interest rates!
                </p>
              </div>
            )}
            
            <div className="mt-4 pt-4 border-t border-red-200">
              <Link 
                to="/app/debt-snowball" 
                className="text-red-700 font-medium hover:text-red-900 flex items-center gap-1"
              >
                📊 {debts.length > 0 ? 'View detailed debt payoff strategies' : 'Go to Debt Calculator'} →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Back Link */}
      <div className="text-center pb-6">
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">
          ← Back to Dashboard
        </Link>
      </div>
    </div>
  );
}

export default QuickWins;
